import { z } from 'zod';
import {
  PluginAttachmentValueSchema,
  SandboxOptionsSchema,
  UserContentBlockSchema,
  type GitRepoAttachmentConfig,
  type PluginAttachmentValue,
} from '@runloop/reflex-contract';
import {
  AgentAuthMethodSchema,
  AgentConfigSchema,
  AgentEnvVarSchema,
  AgentEnvVarInputSchema,
  ModelProviderSecretIdSchema,
  PromptModeSchema,
  ResourceOwnerInputSchema,
  getGitRepoAttachment,
} from '@reflex/shared';

/**
 * User-selectable persona avatar. Stored as a single string with two possible
 * shapes:
 *   - `"lucide:<name>"` — a curated Lucide icon (rendered as an SVG glyph)
 *   - any other non-empty string — treated as an emoji character
 * `null` means "use the fallback" (initials).
 */
export const PersonaIconSchema = z.string().min(1).max(64);

/**
 * Subset of `AgentConfigSchema` that a persona owns at definition time.
 * Persona has no `prompt` (that's the user's task at launch time, captured
 * by `defaultPrompt` instead) and no `content` (launch-only image blocks).
 *
 * By extending `AgentConfigSchema` rather than mirroring its fields, every
 * field added there flows into persona automatically — eliminating the
 * drift that previously made the two definitions diverge (e.g. persona
 * never had `modelEndpointId` until this refactor).
 */
const PersonaConfigSchema = AgentConfigSchema.omit({ prompt: true, content: true });

/** Persona-only metadata fields (never appear on `AgentConfig`). */
const PersonaMetaShape = {
  id: z.string(),
  description: z.string().nullable(),
  icon: PersonaIconSchema.nullable(),
  defaultPrompt: z.string().nullable(),
  /**
   * Owning organization. `null` is the legacy host-shared sentinel for rows
   * that pre-date per-org scoping; every persona created today carries the
   * creating org's id.
   */
  organizationId: z.string().nullable(),
  /**
   * Per-resource owner. A single prefixed id whose kind (`user` / `team`
   * / `organization` / `project` / …) is derived from the prefix. `null`
   * is the host-tier sentinel used by legacy rows that pre-date the
   * resource-ownership feature (visible to all org members). Backfilled
   * rows for organization-owned personas carry the row's `organizationId`
   * as the explicit org sentinel.
   */
  ownerId: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
} as const;

/**
 * Persisted persona row. Every config field is `nullable` (matching the DB
 * row shape — columns are nullable, not optional). Repository information
 * is carried inside `attachments` via the shared `git-repo` plugin
 * attachment, just like agents.
 */
export const AgentPersonaSchema = PersonaConfigSchema.extend({
  // Persona always has a name (overrides AgentConfig's optional name).
  name: z.string(),
  // Persisted-row convention: every config field that is `optional` on
  // AgentConfig becomes `nullable` here. We re-declare each one rather than
  // generating it dynamically so the resulting type is explicit and the
  // mapping is visible at a glance.
  systemPrompt: z.string().nullable(),
  promptMode: PromptModeSchema.nullable(),
  model: z.string().nullable(),
  modelEndpointId: z.string().nullable(),
  /**
   * `mps_*` id of the model-provider secret pinned to this persona for
   * its primary provider. When set, launches resolve this exact key
   * (subject to the owner-scope rule) instead of walking the
   * user -> team -> org precedence. `null` leaves resolution implicit.
   */
  modelProviderSecretId: z.string().nullable(),
  blueprintName: z.string().nullable(),
  /**
   * Generic sandbox-provider options (blueprint, devbox size, …) saved with
   * the persona and applied at launch time. When present, takes precedence
   * over the legacy top-level `blueprintName`.
   */
  sandboxOptions: SandboxOptionsSchema.nullable(),
  attachments: z.array(PluginAttachmentValueSchema).nullable(),
  envVars: z.array(AgentEnvVarSchema).nullable(),
  /**
   * Labels (`alb_*`) every run launched from this persona wears, and the
   * group (`agp_*`) each one is filed into. A persona is a kind of work
   * someone does over and over, which is exactly what a label names — so
   * saying it once on the persona beats saying it on every launch.
   */
  labelIds: z.array(z.string()).nullable(),
  groupId: z.string().nullable(),
  ...PersonaMetaShape,
}).meta({ id: 'AgentPersona' });
export type AgentPersona = z.infer<typeof AgentPersonaSchema>;

/**
 * Input shape for `POST /agent-personas`. Reuses `AgentConfigSchema`'s
 * optional fields directly — clients can omit anything other than `name`
 * and `agentType`. Repo information must be supplied via the `git-repo`
 * attachment inside `attachments`, matching the agent-launch convention.
 */
export const CreateAgentPersonaSchema = PersonaConfigSchema.extend({
  name: z.string().min(1, 'Name is required'),
  agentType: z.string().min(1, 'Agent type is required'),
  description: z.string().optional(),
  icon: PersonaIconSchema.optional(),
  defaultPrompt: z.string().optional(),
  /**
   * `mps_*` id of the model-provider secret to pin to the persona. The
   * server validates it against the persona's owner scope before saving.
   */
  providerSecretId: ModelProviderSecretIdSchema.optional(),
  /**
   * Resource ownership for the new persona: a prefixed owner id
   * (`usr_` / `tem_` / `org_` / `prj_`). Required at runtime — the
   * resource-kind registry has no default scope, so an omitted or
   * host-tier (`null`) owner is a 400 naming the eligible scopes. Routes
   * validate membership via `assertCanWriteOwned`.
   */
  owner: ResourceOwnerInputSchema.optional().describe(
    'Owner of the new resource: a usr_/tem_/org_/prj_ id. Required — there is no default scope.',
  ),
});
export type CreateAgentPersona = z.infer<typeof CreateAgentPersonaSchema>;

/**
 * Patch shape for `PUT /agent-personas/:id`. Every field is `nullable +
 * optional` so callers can distinguish:
 *   - omit the key entirely → leave the field unchanged
 *   - send `null` → clear the field
 *   - send a value → set the field
 */
export const UpdateAgentPersonaSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  icon: PersonaIconSchema.nullable().optional(),
  agentType: z.string().min(1).optional(),
  defaultPrompt: z.string().nullable().optional(),
  systemPrompt: z.string().nullable().optional(),
  promptMode: PromptModeSchema.nullable().optional(),
  model: z.string().nullable().optional(),
  modelEndpointId: z.string().nullable().optional(),
  /**
   * Pin (`mps_*`), clear (`null`), or leave unchanged (omit) the persona's
   * model-provider secret. Validated against the persona's owner scope.
   */
  providerSecretId: ModelProviderSecretIdSchema.nullable().optional(),
  blueprintName: z.string().nullable().optional(),
  /**
   * Patch the persona's `sandboxOptions`. Send the new object to replace,
   * `null` to clear, or omit to leave unchanged.
   */
  sandboxOptions: SandboxOptionsSchema.nullable().optional(),
  attachments: z.array(PluginAttachmentValueSchema).nullable().optional(),
  envVars: z.array(AgentEnvVarSchema).nullable().optional(),
  /** Replace, clear (`null`), or leave unchanged (omit) the persona's labels. */
  labelIds: z.array(z.string()).nullable().optional(),
  /** Pin, clear (`null`), or leave unchanged (omit) the persona's group. */
  groupId: z.string().nullable().optional(),
  /**
   * Resource ownership transfer. When provided, the caller must already
   * be allowed to claim the requested owner. Routes enforce this via
   * {@link assertCallerCanOwn}.
   */
  owner: ResourceOwnerInputSchema.optional(),
});
export type UpdateAgentPersona = z.infer<typeof UpdateAgentPersonaSchema>;

/**
 * Per-launch overrides accepted by `POST /agent-personas/:id/launch`.
 * Carries the user-typed `prompt` (always required at launch time) and any
 * extra attachments / env vars that should layer on top of the persona's
 * own configuration.
 */
export const LaunchFromPersonaSchema = z.object({
  prompt: z.string().min(1, 'Prompt is required'),
  promptStrategy: z.enum(['prepend-default', 'replace']).optional(),
  name: z.string().optional(),
  /**
   * Launch-time agent-type override — "this persona, but run it as
   * `cursor`". The web launch dialog has always allowed this (applying a
   * persona seeds the form, and the user may then switch the type before
   * submitting to `POST /agents`); this field is what gives the API the
   * same reach without the caller re-materializing the persona's config.
   *
   * Validated server-side against the live agent-model support catalog:
   * the type must be registered and enabled for the org. When it differs
   * from the persona's own type, the fields that depend on it — model,
   * `modelEndpointId`, and the persona's pinned provider key — are
   * re-resolved for the new type rather than carried over, mirroring the
   * dialog's `set_agent_type` reset.
   */
  agentType: z.string().min(1).optional(),
  model: z.string().optional(),
  /**
   * Endpoint route (`modelEndpointId`) for the launch. Normally left
   * unset — the server picks the endpoint compatible with the effective
   * agent type and model. Supply it only to pin a specific route.
   */
  modelEndpointId: z.string().min(1).optional(),
  /**
   * Launch-time system-prompt override. Replaces the persona's own
   * `systemPrompt` for this run only; the persona is never mutated. Matches
   * the launch dialog, where the applied persona's system prompt is editable
   * before submitting.
   */
  systemPrompt: z.string().optional(),
  /** Launch-time prompt mode (`implement` / `plan`), as in the launch dialog. */
  promptMode: PromptModeSchema.optional(),
  /**
   * Explicit auth method for the launch. Usually derived from
   * `providerSecretId`'s row type instead; set it to opt into a personal
   * subscription (`claude-max`, `codex-subscription`) without naming a
   * specific key.
   */
  authMethod: AgentAuthMethodSchema.optional(),
  /**
   * Launch-time override of the persona's pinned model-provider secret.
   * Resolved against the launching user's visibility and the persona's
   * owner scope. When omitted, the persona's own pin (if any) is used.
   */
  providerSecretId: ModelProviderSecretIdSchema.optional(),
  content: z.array(UserContentBlockSchema).optional(),
  extraAttachments: z.array(PluginAttachmentValueSchema).optional(),
  /**
   * Launch-time env vars use the input schema (accepts inline values or
   * selected org secret refs) rather than the persisted schema. Server-side
   * validation ensures selected refs exist in the active organization.
   */
  extraEnvVars: z.array(AgentEnvVarInputSchema).optional(),
  /**
   * Launch-time repository override. Fills in the start config's repo when
   * the persona itself doesn't pin a repo (e.g. the built-in Implementation
   * persona, where Slack resolves the repo from the prompt). Takes
   * precedence over the persona's own git-repo attachment when both are
   * set. The value is normalized into a `git-repo` plugin attachment by
   * the launcher.
   */
  repoSlug: z.string().optional(),
  repoBranch: z.string().optional(),
  /** Labels added to the persona's own for this launch. */
  extraLabelIds: z.array(z.string()).optional(),
  /** Group for this launch, replacing the persona's own when both are set. */
  groupId: z.string().optional(),
  /** @deprecated Use `sandboxOptions.blueprintName` instead. */
  blueprintName: z.string().optional(),
  /**
   * Launch-time sandbox-provider option overrides. When set, fields here
   * win over the persona's own `sandboxOptions` (and the deprecated
   * top-level `blueprintName`).
   */
  sandboxOptions: SandboxOptionsSchema.optional(),
});
export type LaunchFromPersona = z.infer<typeof LaunchFromPersonaSchema>;

/**
 * Read the repo slug + branch carried by a persona's `git-repo` attachment.
 * Returns `undefined` when the persona has no repo configured.
 *
 * This mirrors `getGitRepoAttachment(agent)` from `@reflex/shared` —
 * agents and personas store their repo the same way (a `git-repo` plugin
 * attachment), so the same helper works for both.
 */
export function getPersonaRepo(persona: {
  attachments?: PluginAttachmentValue[] | null;
}): GitRepoAttachmentConfig | undefined {
  return getGitRepoAttachment(persona);
}
